import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, GripVertical, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Block {
  id: string;
  content: React.ReactNode;
  /** Shown in the "hidden blocks" bar so a hidden block can be found again. */
  label?: string;
}

interface Props {
  storageKey: string;
  blocks: Block[];
}

/**
 * Dashboard layout — drag to reorder, and hide/show per block.
 *
 * TS Lot 2 §3.1.1 asks for "drag-and-drop of blocks, hide/show, saving to the
 * profile". Hide/show was missing, which the client review AL-2026/08-04-01
 * recorded as finding D-02.
 *
 * ⚠ The layout persists to **localStorage**, so it follows the browser rather
 * than the user profile: signing in elsewhere starts from the default order.
 * Storing it against the profile needs a `User` column and is flagged in the
 * written reply rather than assumed.
 */
export function DraggableBlocksContainer({ storageKey, blocks }: Props) {
  const { t } = useTranslation('common');
  const hiddenKey = `${storageKey}.hidden`;

  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(hiddenKey);
      const parsed = saved ? (JSON.parse(saved) as string[]) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const persistHidden = useCallback(
    (next: string[]) => {
      setHidden(next);
      try {
        localStorage.setItem(hiddenKey, JSON.stringify(next));
      } catch {
        /* storage unavailable (private mode) — the session still works */
      }
    },
    [hiddenKey],
  );

  const hide = useCallback(
    (id: string) => persistHidden([...hidden.filter(h => h !== id), id]),
    [hidden, persistHidden],
  );
  const show = useCallback(
    (id: string) => persistHidden(hidden.filter(h => h !== id)),
    [hidden, persistHidden],
  );

  return (
    <DraggableBlocksInner
      storageKey={storageKey}
      blocks={blocks}
      hidden={hidden}
      onHide={hide}
      onShow={show}
      onShowAll={() => persistHidden([])}
      t={t}
    />
  );
}

function DraggableBlocksInner({
  storageKey,
  blocks,
  hidden,
  onHide,
  onShow,
  onShowAll,
  t,
}: Props & {
  hidden: string[];
  onHide: (id: string) => void;
  onShow: (id: string) => void;
  onShowAll: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [order, setOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        const allIds = blocks.map(b => b.id);
        const reordered = parsed.filter(id => allIds.includes(id));
        // Blocks added after the order was saved slot in at their code
        // position (e.g. new top cards stay on top) instead of the end.
        allIds.forEach((id, idx) => {
          if (!reordered.includes(id)) reordered.splice(Math.min(idx, reordered.length), 0, id);
        });
        return reordered;
      }
    } catch {}
    return blocks.map(b => b.id);
  });

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const draggedId = useRef<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, blockId: string) => {
    draggedId.current = blockId;
    setDraggingId(blockId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId.current || draggedId.current === targetId) return;
    setOrder(prev => {
      const fromIdx = prev.indexOf(draggedId.current!);
      const toIdx = prev.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, draggedId.current!);
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    draggedId.current = null;
    setOrder(current => {
      localStorage.setItem(storageKey, JSON.stringify(current));
      return current;
    });
  }, [storageKey]);

  const sortedBlocks = order
    .map(id => blocks.find(b => b.id === id))
    .filter((b): b is Block => b != null);

  const visibleBlocks = sortedBlocks.filter(b => !hidden.includes(b.id));
  const hiddenBlocks = sortedBlocks.filter(b => hidden.includes(b.id));

  return (
    <div className="px-4 pt-4 pb-4">
      {/* D-02 — hidden blocks stay discoverable; a hide with no way back is a
          trap, not a preference. */}
      {hiddenBlocks.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-[0.75rem] font-medium text-slate-500">
            {t('dashboard.hiddenBlocks', { count: hiddenBlocks.length })}
          </span>
          {hiddenBlocks.map(b => (
            <button
              key={b.id}
              type="button"
              onClick={() => onShow(b.id)}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[0.75rem] text-slate-600 transition hover:border-[#0069D1] hover:text-[#0069D1]"
            >
              <Eye size={12} />
              {b.label ?? b.id}
            </button>
          ))}
          <button
            type="button"
            onClick={onShowAll}
            className="ml-auto inline-flex items-center gap-1 text-[0.75rem] font-medium text-[#0069D1] hover:underline"
          >
            <RotateCcw size={12} />
            {t('dashboard.showAllBlocks')}
          </button>
        </div>
      )}

      {visibleBlocks.map(block => (
        <div
          key={block.id}
          draggable
          data-block-id={block.id}
          onDragStart={e => handleDragStart(e, block.id)}
          onDragOver={e => handleDragOver(e, block.id)}
          onDragEnd={handleDragEnd}
          className={cn(
            'relative mb-6 last:mb-0 group pl-5',
            // A block whose content renders null (e.g. "Awaiting my action"
            // when empty) leaves only the two chrome children — the grip and
            // the hide button — so the whole row is hidden.
            // ⚠ Keep this index in step with the number of chrome children
            // rendered before `block.content` (currently 2 → content is 3rd).
            '[&:not(:has(>*:nth-child(3)))]:hidden',
            draggingId === block.id && 'opacity-50'
          )}
        >
          <div className="absolute top-3 left-0 w-5 h-7 flex items-center justify-center text-slate-300 cursor-grab opacity-0 group-hover:opacity-100 hover:text-[#0069D1] transition-opacity z-10">
            <GripVertical size={16} />
          </div>
          <button
            type="button"
            onClick={() => onHide(block.id)}
            title={t('dashboard.hideBlock')}
            aria-label={t('dashboard.hideBlock')}
            className="absolute top-3 right-3 z-10 flex h-7 w-7 items-center justify-center rounded-md text-slate-300 opacity-0 transition-opacity hover:bg-slate-50 hover:text-[#0069D1] group-hover:opacity-100"
          >
            <EyeOff size={15} />
          </button>
          {block.content}
        </div>
      ))}
    </div>
  );
}
